export type FormFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'DROPDOWN' | 'CHECKBOX' | 'FILE_UPLOAD' | 'PERSON'
export type FormResponseStatus = 'PENDING' | 'REVIEWED' | 'EXPORTED' | 'REJECTED'

export type FormLogicOperator = 'EQUALS' | 'NOT_EQUALS' | 'CONTAINS' | 'GREATER_THAN' | 'LESS_THAN'
export type FormLogicAction = 'SHOW' | 'HIDE' | 'REQUIRE' | 'SKIP_TO'

export type FormLogicRule = {
  id: string
  when: { fieldId: string; operator: FormLogicOperator; value: unknown }
  then: { action: FormLogicAction; targetFieldId: string }
}

export type FormLogic = { version?: number; rules: FormLogicRule[] }

export type FormFieldOption = { id: string; label: string; value: string }

export type PersonFieldPickScope = 'MEMBERS' | 'MEMBERS_AND_HELPERS'

export type FormValidationOptions = {
  helperUids?: ReadonlySet<string>
}

export const hasMembersOnlyPersonField = (fields: unknown): boolean => {
  if (!Array.isArray(fields)) return false
  return fields.some(
    (field) =>
      Boolean(field) &&
      typeof field === 'object' &&
      (field as { type?: unknown }).type === 'PERSON' &&
      (field as { pickScope?: unknown }).pickScope === 'MEMBERS',
  )
}

export type FormFieldValidator = {
  id: string
  type: FormFieldType
  label?: string
  required?: boolean
  validation?: {
    minLength?: number
    maxLength?: number
    pattern?: string
    min?: number
    max?: number
    step?: number
    minDate?: string
    maxDate?: string
    minSelections?: number
    maxSelections?: number
    maxFiles?: number
    maxFileSizeBytes?: number
    acceptedMimeTypes?: string[]
  }
  options?: FormFieldOption[]
  allowMultiple?: boolean
  pickScope?: PersonFieldPickScope
}

export type FormDefinitionLike = {
  fields: FormFieldValidator[]
  logic?: FormLogic | null
}

const empty = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

export const matchesRule = (rule: FormLogicRule, values: Record<string, unknown>): boolean => {
  const current = values[rule.when.fieldId]
  const expected = rule.when.value
  if (rule.when.operator === 'EQUALS') return String(current ?? '') === String(expected ?? '')
  if (rule.when.operator === 'NOT_EQUALS') return String(current ?? '') !== String(expected ?? '')
  if (rule.when.operator === 'CONTAINS') {
    return Array.isArray(current)
      ? current.map(String).includes(String(expected))
      : String(current ?? '').includes(String(expected ?? ''))
  }
  if (rule.when.operator === 'GREATER_THAN') return Number(current) > Number(expected)
  return Number(current) < Number(expected)
}

export const isFieldVisible = (
  field: FormFieldValidator,
  logic: FormLogic | null | undefined,
  values: Record<string, unknown>,
): boolean => {
  if (!logic?.rules?.length) return true
  const rules = logic.rules.filter(
    (rule) => rule.then.targetFieldId === field.id && (rule.then.action === 'SHOW' || rule.then.action === 'HIDE'),
  )
  return rules.every((rule) => (rule.then.action === 'SHOW' ? matchesRule(rule, values) : !matchesRule(rule, values)))
}

export const isFieldRequired = (
  field: FormFieldValidator,
  logic: FormLogic | null | undefined,
  values: Record<string, unknown>,
): boolean => {
  if (field.required) return true
  if (!logic?.rules?.length) return false
  return logic.rules.some((rule) => rule.then.targetFieldId === field.id && rule.then.action === 'REQUIRE' && matchesRule(rule, values))
}

export const getVisibleFields = (
  definition: FormDefinitionLike,
  values: Record<string, unknown>,
): FormFieldValidator[] => {
  return (definition.fields ?? []).filter((field) => isFieldVisible(field, definition.logic, values))
}

export const validateResponsePayload = (
  definition: FormDefinitionLike,
  values: Record<string, unknown>,
  options?: FormValidationOptions,
): Record<string, string> => {
  const errors: Record<string, string> = {}
  for (const field of getVisibleFields(definition, values)) {
    const value = values[field.id]
    const required = isFieldRequired(field, definition.logic, values)
    if (required && empty(value)) {
      errors[field.id] = 'This field is required.'
      continue
    }
    if (field.type === 'CHECKBOX' && required && value !== true) {
      errors[field.id] = 'This checkbox must be selected.'
      continue
    }
    if (empty(value)) continue

    if (field.type === 'TEXT') {
      const text = String(value)
      if (field.validation?.minLength !== undefined && text.length < field.validation.minLength) {
        errors[field.id] = `Enter at least ${field.validation.minLength} characters.`
      } else if (field.validation?.maxLength !== undefined && text.length > field.validation.maxLength) {
        errors[field.id] = `Use no more than ${field.validation.maxLength} characters.`
      } else if (field.validation?.pattern) {
        try {
          if (!new RegExp(field.validation.pattern).test(text)) {
            errors[field.id] = 'Use the required format.'
          }
        } catch {
          errors[field.id] = 'This field has an invalid format rule.'
        }
      }
    } else if (field.type === 'NUMBER') {
      const number = Number(value)
      if (!Number.isFinite(number)) {
        errors[field.id] = 'Enter a valid number.'
      } else if (field.validation?.min !== undefined && number < field.validation.min) {
        errors[field.id] = `Minimum value is ${field.validation.min}.`
      } else if (field.validation?.max !== undefined && number > field.validation.max) {
        errors[field.id] = `Maximum value is ${field.validation.max}.`
      }
    } else if (field.type === 'DATE') {
      const date = String(value)
      if (field.validation?.minDate && date < field.validation.minDate) {
        errors[field.id] = `Choose ${field.validation.minDate} or later.`
      } else if (field.validation?.maxDate && date > field.validation.maxDate) {
        errors[field.id] = `Choose ${field.validation.maxDate} or earlier.`
      }
    } else if (field.type === 'DROPDOWN') {
      const allowed = new Set((field.options ?? []).map((option) => option.value))
      const selected = field.allowMultiple
        ? Array.isArray(value)
          ? value.map(String)
          : []
        : [String(value)]
      if (selected.some((entry) => !allowed.has(entry))) {
        errors[field.id] = 'Choose an available option.'
      } else if (field.validation?.minSelections !== undefined && selected.length < field.validation.minSelections) {
        errors[field.id] = `Choose at least ${field.validation.minSelections} option${field.validation.minSelections === 1 ? '' : 's'}.`
      } else if (field.validation?.maxSelections !== undefined && selected.length > field.validation.maxSelections) {
        errors[field.id] = `Choose no more than ${field.validation.maxSelections} option${field.validation.maxSelections === 1 ? '' : 's'}.`
      }
    } else if (field.type === 'FILE_UPLOAD') {
      const isFileLike = (entry: unknown): entry is { size: number; type: string } =>
        Boolean(entry && typeof entry === 'object' && typeof (entry as { size?: unknown }).size === 'number' && typeof (entry as { type?: unknown }).type === 'string')
      const files = Array.isArray(value) ? value.filter(isFileLike) : []
      if (field.validation?.maxFiles !== undefined && files.length > field.validation.maxFiles) {
        errors[field.id] = `Upload no more than ${field.validation.maxFiles} files.`
      } else if (field.validation?.maxFileSizeBytes !== undefined && files.some((file) => file.size > field.validation!.maxFileSizeBytes!)) {
        errors[field.id] = 'One or more files exceed the size limit.'
      } else if (field.validation?.acceptedMimeTypes?.length && files.some((file) => !field.validation!.acceptedMimeTypes!.includes(file.type))) {
        errors[field.id] = 'One or more files use an unsupported type.'
      }
    } else if (field.type === 'PERSON') {
      const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0
      const isHelper = (uid: string): boolean => {
        if (!options?.helperUids) return false
        return options.helperUids.has(uid)
      }

      if (field.allowMultiple) {
        const isStringArray = Array.isArray(value) && value.length > 0 && value.every(isNonEmptyString)
        const isSingleString = isNonEmptyString(value)
        if (!isStringArray && !isSingleString) {
          errors[field.id] = 'Choose a valid person.'
        } else {
          const selected = Array.isArray(value) ? value : [value]
          if (field.validation?.minSelections !== undefined && selected.length < field.validation.minSelections) {
            errors[field.id] = `Choose at least ${field.validation.minSelections} option${field.validation.minSelections === 1 ? '' : 's'}.`
          } else if (field.validation?.maxSelections !== undefined && selected.length > field.validation.maxSelections) {
            errors[field.id] = `Choose no more than ${field.validation.maxSelections} option${field.validation.maxSelections === 1 ? '' : 's'}.`
          }
          if (field.pickScope === 'MEMBERS' && selected.some(isHelper)) {
            errors[field.id] = 'Helpers cannot be selected for this field.'
          }
        }
      } else {
        if (!isNonEmptyString(value)) {
          errors[field.id] = 'Choose a valid person.'
        } else if (field.pickScope === 'MEMBERS' && isHelper(value)) {
          errors[field.id] = 'Helpers cannot be selected for this field.'
        }
      }
    }
  }
  return errors
}
